/**
 * Collection rewards: the requirement model shared by gallery frame styles and friend stickers, the
 * frame catalogue, and the sticker album (one page per world + a page of friends).
 */
import type { PathId } from '../content/model';
import type { CreatureSubject } from './creatures';

/** Progress numbers a reward can depend on (all only ever grow, so nothing is ever taken away). */
export interface CollectStats {
  /** Drawings in the journal (calibration excluded). */
  drawings: number;
  /** Sparks level 1..7. */
  level: number;
  /** Weeks whose rhythm goal was met (garden flowers). */
  blooms: number;
  /** Discovered creature-book cards. */
  creatures: number;
}

export type CollectReq =
  | { kind: 'free' }
  | { kind: 'drawings'; n: number }
  | { kind: 'level'; n: number }
  | { kind: 'blooms'; n: number }
  | { kind: 'creatures'; n: number };

export function reqProgress(req: CollectReq, s: CollectStats): { have: number; need: number; met: boolean } {
  if (req.kind === 'free') return { have: 1, need: 1, met: true };
  const have = req.kind === 'drawings' ? s.drawings : req.kind === 'level' ? s.level : req.kind === 'blooms' ? s.blooms : s.creatures;
  return { have: Math.min(have, req.n), need: req.n, met: have >= req.n };
}
export const reqMet = (req: CollectReq, s: CollectStats) => reqProgress(req, s).met;

/* ------------------------------------ Frames ------------------------------------ */

export type FrameId = 'wood' | 'cream' | 'crayon' | 'pastel' | 'gold' | 'starry' | 'rainbow';

export interface FrameStyle {
  id: FrameId;
  req: CollectReq;
}

/** Gallery wall frame styles, in unlock order. */
export const FRAME_STYLES: FrameStyle[] = [
  { id: 'wood', req: { kind: 'free' } },
  { id: 'cream', req: { kind: 'drawings', n: 3 } },
  { id: 'crayon', req: { kind: 'blooms', n: 1 } },
  { id: 'pastel', req: { kind: 'level', n: 3 } },
  { id: 'gold', req: { kind: 'creatures', n: 12 } },
  { id: 'starry', req: { kind: 'blooms', n: 4 } },
  { id: 'rainbow', req: { kind: 'level', n: 6 } },
];

export const frameStyle = (id: string | undefined): FrameStyle => FRAME_STYLES.find((f) => f.id === id) ?? FRAME_STYLES[0]!;
export const unlockedFrames = (s: CollectStats): FrameId[] => FRAME_STYLES.filter((f) => reqMet(f.req, s)).map((f) => f.id);

/* ------------------------------------ Album ------------------------------------ */

export type AlbumPageId = PathId | 'friends';

export interface AlbumSticker {
  /** `s:<drawingId>` (subject) or `f:<cast>:<pose>` (friend). */
  id: string;
  page: AlbumPageId;
  kind: 'subject' | 'friend';
  drawingId?: string;
  cast?: 'scribble' | 'juno';
  pose?: string;
  req?: CollectReq;
}

/** Friend stickers (cast poses), earned with milestones. */
export const FRIEND_STICKERS: AlbumSticker[] = (
  [
    ['scribble', 'happy', { kind: 'drawings', n: 1 }],
    ['scribble', 'wave', { kind: 'level', n: 2 }],
    ['juno', 'smile', { kind: 'blooms', n: 1 }],
    ['scribble', 'curious', { kind: 'drawings', n: 5 }],
    ['scribble', 'cheer', { kind: 'level', n: 3 }],
    ['juno', 'laugh', { kind: 'blooms', n: 3 }],
    ['scribble', 'proud', { kind: 'creatures', n: 16 }],
    ['juno', 'proud', { kind: 'drawings', n: 25 }],
  ] as const
).map(([cast, pose, req]) => ({ id: `f:${cast}:${pose}`, page: 'friends' as const, kind: 'friend' as const, cast, pose, req }));

export interface AlbumSlot {
  sticker: AlbumSticker;
  earned: boolean;
  placed: boolean;
}

export interface AlbumPage {
  id: AlbumPageId;
  slots: AlbumSlot[];
  earned: number;
  placed: number;
}

/**
 * Album pages: each world's path lessons (one sticker each, earned by drawing it), then the friends
 * page. `placed` = stickers the child already peeled and stuck in.
 */
export function stickerAlbum(subjects: CreatureSubject[], drawnIds: ReadonlySet<string>, stats: CollectStats, placed: readonly string[]): AlbumPage[] {
  const placedSet = new Set(placed);
  const pages = new Map<AlbumPageId, AlbumPage>();
  const add = (st: AlbumSticker, earned: boolean) => {
    let p = pages.get(st.page);
    if (!p) pages.set(st.page, (p = { id: st.page, slots: [], earned: 0, placed: 0 }));
    const isPlaced = earned && placedSet.has(st.id);
    p.slots.push({ sticker: st, earned, placed: isPlaced });
    if (earned) p.earned++;
    if (isPlaced) p.placed++;
  };
  for (const s of subjects) if (s.onPath) add({ id: `s:${s.drawingId}`, page: s.world, kind: 'subject', drawingId: s.drawingId }, drawnIds.has(s.drawingId));
  for (const f of FRIEND_STICKERS) add(f, reqMet(f.req!, stats));
  return [...pages.values()];
}

/** Earned stickers not stuck in yet (the "new stickers" tray). */
export const albumTray = (pages: AlbumPage[]): AlbumSlot[] => pages.flatMap((p) => p.slots.filter((s) => s.earned && !s.placed));
